import { LoadingButton } from '@mui/lab';
import { Grid, Stack } from '@mui/material';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { TemporaryOrganizationDataTypes } from 'src/api/survey-edit/useTemporaryOrganizationApi';
import { ORG_LEVEL_TYPE } from 'src/constants';
import { useAppSelector } from 'src/redux/store';
import { CreateOrgTypeList } from '../../../tab-containers/shared-tabs/temporary-organization/components/TemporaryOrganizationTab';
import useTemporaryOrganizationModal from '../hooks/useTemporaryOrganizationModal';
import SegmentInputForm from './SegmentInputForm';
import SegmentPullDown from './SegmentPullDown';

interface componentTypes {
  inputData: TemporaryOrganizationDataTypes;
  createType: CreateOrgTypeList | null;
  isEdit: boolean;
  surveyDetailId: string;
}

export enum SegmentTypeList {
  company = 'company',
  division = 'division',
  sbu = 'sbu',
  department = 'department',
  group = 'group',
  region = 'region',
  country = 'country',
  rank = 'rank',
}

export enum orgLevelTypeList {
  COMPANY = 'COMPANY',
  DIVISION = 'DIVISION',
  SBU = 'SBU',
  DEPARTMENT = 'DEPARTMENT',
}

const TemporaryOrganizationContent: React.FC<componentTypes> = memo((props) => {
  const { inputData, createType, isEdit, surveyDetailId } = props;
  const { t } = useTranslation();

  const {
    companyCode,
    companyNameJpn,
    divisionNameJpn,
    sbuNameJpn,
    departmentNameJpn,
    groupNameJpn,
    companyNameEng,
    divisionNameEng,
    sbuNameEng,
    departmentNameEng,
    groupNameEng,
    isCheckDivision,
    isCheckSbu,
    isCheckDepartment,
    isCheckGroup,
    selectAutocompleteValues,
    divisionOptionsList,
    sbuOptionsList,
    departmentOptionsList,
    groupOptionsList,
    autocompleteDivisionProps,
    autocompleteSbuProps,
    autocompleteDepartmentProps,
    autocompleteGroupProps,
    autocompleteRegionProps,
    autocompleteCountryProps,
    autocompleteRankProps,
    baseOrganizationData,
    isLoadingInitialData,
    setCompanyNameJpn,
    setDivisionNameJpn,
    setSbuNameJpn,
    setDepartmentNameJpn,
    setGroupNameJpn,
    setCompanyNameEng,
    setDivisionNameEng,
    setSbuNameEng,
    setDepartmentNameEng,
    setGroupNameEng,
    callbackCheckBoxDivision,
    callbackCheckBoxSbu,
    callbackCheckBoxDepartment,
    callbackCheckBoxGroup,
    insertData,
    updateData,
  } = useTemporaryOrganizationModal({ inputData, createType, surveyDetailId });

  const isDisabledCompany = (isEdit && inputData.orgLevelType !== ORG_LEVEL_TYPE.COMPANY) || createType === CreateOrgTypeList.ttc;
  const isDisabledDivision = isEdit && inputData.orgLevelType !== ORG_LEVEL_TYPE.DIVISION;
  const isDisabledSbu = isEdit && inputData.orgLevelType !== ORG_LEVEL_TYPE.SBU;
  const isDisabledDepartment = isEdit && inputData.orgLevelType !== ORG_LEVEL_TYPE.DEPARTMENT;
  const isDisabledGroup = isEdit && inputData.orgLevelType !== ORG_LEVEL_TYPE.GROUP;

  // ローディング状態
  const isLoading = useAppSelector((state) => state.temporaryOrganization.isLoading);
  // バリデーション
  const isErrorCompanyNames = useAppSelector((state) => state.temporaryOrganization.isErrorCompanyNames);
  const isErrorDivisionNames = useAppSelector((state) => state.temporaryOrganization.isErrorDivisionNames);
  const isErrorDivisionPullDown = useAppSelector((state) => state.temporaryOrganization.isErrorDivisionPullDown);
  const isErrorSbuNames = useAppSelector((state) => state.temporaryOrganization.isErrorSbuNames);
  const isErrorSbuPullDown = useAppSelector((state) => state.temporaryOrganization.isErrorSbuPullDown);
  const isErrorDepartmentNames = useAppSelector((state) => state.temporaryOrganization.isErrorDepartmentNames);
  const isErrorDepartmentPullDown = useAppSelector((state) => state.temporaryOrganization.isErrorDepartmentPullDown);
  const isErrorGroupNames = useAppSelector((state) => state.temporaryOrganization.isErrorGroupNames);
  const isErrorGroupPullDown = useAppSelector((state) => state.temporaryOrganization.isErrorGroupPullDown);
  const isErrorRegionPullDown = useAppSelector((state) => state.temporaryOrganization.isErrorRegionPullDown);
  const isErrorCountryPullDown = useAppSelector((state) => state.temporaryOrganization.isErrorCountryPullDown);
  const isErrorRankPullDown = useAppSelector((state) => state.temporaryOrganization.isErrorRankPullDown);

  return (
    <>
      {/** Company **/}
      <SegmentInputForm
        segmentNameJpn={t('enqueteCreate.temporaryOrganization.modal.form.companyNameJpn')}
        segmentNameEng={t('enqueteCreate.temporaryOrganization.modal.form.companyNameEng')}
        segmentDataJpn={companyNameJpn}
        setSegmentDataJpn={setCompanyNameJpn}
        segmentDataEng={companyNameEng}
        setSegmentDataEng={setCompanyNameEng}
        isEdit={isEdit}
        isChecked={null}
        isDisabledForm={isDisabledCompany}
        optionsList={[]}
        autocompleteProps={null}
        checkboxChangeCallback={null}
        validationNames={isErrorCompanyNames}
        validationPullDown={null}
      />

      {/** Division **/}
      <SegmentInputForm
        segmentNameJpn={
          isDisabledCompany
            ? t('enqueteCreate.temporaryOrganization.modal.form.divisionTypeTtcNameJpn')
            : t('enqueteCreate.temporaryOrganization.modal.form.divisionTypeGroupNameJpn')
        }
        segmentNameEng={
          isDisabledCompany
            ? t('enqueteCreate.temporaryOrganization.modal.form.divisionTypeTtcNameEng')
            : t('enqueteCreate.temporaryOrganization.modal.form.divisionTypeGroupNameEng')
        }
        segmentDataJpn={divisionNameJpn}
        setSegmentDataJpn={setDivisionNameJpn}
        segmentDataEng={divisionNameEng}
        setSegmentDataEng={setDivisionNameEng}
        isEdit={isEdit}
        isChecked={isCheckDivision}
        isDisabledForm={isDisabledDivision}
        optionsList={divisionOptionsList}
        autocompleteProps={autocompleteDivisionProps}
        checkboxChangeCallback={callbackCheckBoxDivision}
        validationNames={isErrorDivisionNames}
        validationPullDown={isErrorDivisionPullDown}
      />

      {/** SBU **/}
      <SegmentInputForm
        segmentNameJpn={
          isDisabledCompany
            ? t('enqueteCreate.temporaryOrganization.modal.form.sbuTypeTtcNameJpn')
            : t('enqueteCreate.temporaryOrganization.modal.form.sbuTypeGroupNameJpn')
        }
        segmentNameEng={
          isDisabledCompany
            ? t('enqueteCreate.temporaryOrganization.modal.form.sbuTypeTtcNameEng')
            : t('enqueteCreate.temporaryOrganization.modal.form.sbuTypeGroupNameEng')
        }
        segmentDataJpn={sbuNameJpn}
        setSegmentDataJpn={setSbuNameJpn}
        segmentDataEng={sbuNameEng}
        setSegmentDataEng={setSbuNameEng}
        isEdit={isEdit}
        isChecked={isCheckSbu}
        isDisabledForm={isDisabledSbu}
        optionsList={sbuOptionsList}
        autocompleteProps={autocompleteSbuProps}
        checkboxChangeCallback={callbackCheckBoxSbu}
        validationNames={isErrorSbuNames}
        validationPullDown={isErrorSbuPullDown}
      />

      {/** Department **/}
      <SegmentInputForm
        segmentNameJpn={
          isDisabledCompany
            ? t('enqueteCreate.temporaryOrganization.modal.form.departmentTypeTtcNameJpn')
            : t('enqueteCreate.temporaryOrganization.modal.form.departmentTypeGroupNameJpn')
        }
        segmentNameEng={
          isDisabledCompany
            ? t('enqueteCreate.temporaryOrganization.modal.form.departmentTypeTtcNameEng')
            : t('enqueteCreate.temporaryOrganization.modal.form.departmentTypeGroupNameEng')
        }
        segmentDataJpn={departmentNameJpn}
        setSegmentDataJpn={setDepartmentNameJpn}
        segmentDataEng={departmentNameEng}
        setSegmentDataEng={setDepartmentNameEng}
        isEdit={isEdit}
        isChecked={isCheckDepartment}
        isDisabledForm={isDisabledDepartment}
        optionsList={departmentOptionsList}
        autocompleteProps={autocompleteDepartmentProps}
        checkboxChangeCallback={callbackCheckBoxDepartment}
        validationNames={isErrorDepartmentNames}
        validationPullDown={isErrorDepartmentPullDown}
      />

      {/** Group **/}
      <SegmentInputForm
        segmentNameJpn={
          isDisabledCompany
            ? t('enqueteCreate.temporaryOrganization.modal.form.groupTypeTtcNameJpn')
            : t('enqueteCreate.temporaryOrganization.modal.form.groupTypeGroupNameJpn')
        }
        segmentNameEng={
          isDisabledCompany
            ? t('enqueteCreate.temporaryOrganization.modal.form.groupTypeTtcNameEng')
            : t('enqueteCreate.temporaryOrganization.modal.form.groupTypeGroupNameEng')
        }
        segmentDataJpn={groupNameJpn}
        setSegmentDataJpn={setGroupNameJpn}
        segmentDataEng={groupNameEng}
        setSegmentDataEng={setGroupNameEng}
        isEdit={isEdit}
        isChecked={createType === CreateOrgTypeList.group ? isCheckGroup : null}
        isDisabledForm={isDisabledGroup}
        optionsList={groupOptionsList}
        autocompleteProps={autocompleteGroupProps}
        checkboxChangeCallback={callbackCheckBoxGroup}
        validationNames={isErrorGroupNames}
        validationPullDown={isErrorGroupPullDown}
      />

      {/** 単体作成/編集時は非表示 **/}
      {((isEdit && companyCode !== 'S500') || (!isEdit && createType !== CreateOrgTypeList.ttc)) && (
        <Grid container>
          {/** Region **/}
          <Grid item xs={4} sx={{ mr: 2 }}>
            <SegmentPullDown
              id={autocompleteRegionProps.id}
              label={autocompleteRegionProps.label}
              keyCode={autocompleteRegionProps.key}
              list={autocompleteRegionProps.list}
              onChange={autocompleteRegionProps.onChange}
              validationPullDown={isErrorRegionPullDown}
            />
          </Grid>

          {/** Company **/}
          <Grid item xs={4} sx={{ mr: 2 }}>
            <SegmentPullDown
              id={autocompleteCountryProps.id}
              label={autocompleteCountryProps.label}
              keyCode={autocompleteCountryProps.key}
              list={autocompleteCountryProps.list}
              onChange={autocompleteCountryProps.onChange}
              validationPullDown={isErrorCountryPullDown}
              forceDisabled={selectAutocompleteValues.regionCode === '' && baseOrganizationData.regionCode === ''}
            />
          </Grid>

          {/** Rank **/}
          <Grid item xs={3}>
            <SegmentPullDown
              id={autocompleteRankProps.id}
              label={autocompleteRankProps.label}
              keyCode={autocompleteRankProps.key}
              list={autocompleteRankProps.list}
              onChange={autocompleteRankProps.onChange}
              validationPullDown={isErrorRankPullDown}
            />
          </Grid>
        </Grid>
      )}

      {/* modal bottom actions */}
      <Grid container sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <Stack direction={'row-reverse'}>
            <LoadingButton
              variant="contained"
              type="button"
              loading={isLoading || isLoadingInitialData}
              onClick={() => {
                isEdit ? updateData() : insertData();
              }}>
              {isEdit ? t('button.save') : t('button.create')}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
});

export default TemporaryOrganizationContent;
